import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Wrap,
  WrapItem,
  Tag,
  Text,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Skeleton,
  Divider,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../services/apiClient';
import { useCustomContentList, useContentTags, useDeleteContent } from '../hooks/useCustomContent';
import { UploadContentModal } from '../components/UploadContentModal';
import { EditContentModal } from '../components/EditContentModal';
import { DeleteConflictDialog } from '../components/DeleteConflictDialog';
import { LibraryTabs } from '../components/LibraryTabs';
import { formatTrackDuration } from '../utils';
import { getContentStreamUrl } from '../api/mediaApi';
import { PreviewButton } from '../components/AudioPreview';
import type { AudioContentDto, DeleteConflictResponse, ProcessingStatus } from '../types';

function StatusBadge({ status }: { status: ProcessingStatus }) {
  const { t } = useTranslation();
  if (status === 'completed') return null;
  const scheme = status === 'failed' ? 'red' : 'yellow';
  return (
    <Badge colorScheme={scheme} fontSize="0.6rem">
      {t(`customContent.status.${status}`)}
    </Badge>
  );
}

export function CustomContentPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const uploadModal = useDisclosure();

  const [editing, setEditing] = useState<AudioContentDto | null>(null);
  const [conflict, setConflict] = useState<DeleteConflictResponse | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data: tagsData } = useContentTags();
  const params = useMemo(
    () => ({ tags: tag ? [tag] : undefined, q: search || undefined }),
    [tag, search],
  );
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useCustomContentList(params);
  const deleteContent = useDeleteContent();

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  const handleDelete = async (id: string) => {
    setPendingDeleteId(id);
    try {
      await deleteContent.mutateAsync({ id });
      toast({ status: 'success', title: t('customContent.deleted'), duration: 2000 });
      setPendingDeleteId(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflict(e.details as DeleteConflictResponse);
      } else {
        toast({ status: 'error', title: t('common.error', 'Something went wrong') });
        setPendingDeleteId(null);
      }
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteContent.mutateAsync({ id: pendingDeleteId, confirm: true });
      toast({ status: 'success', title: t('customContent.movedToTrash'), duration: 2000 });
    } catch {
      toast({ status: 'error', title: t('common.error', 'Something went wrong') });
    }
    setConflict(null);
    setPendingDeleteId(null);
  };

  return (
    <Box>
      <LibraryTabs />

      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="lg" color="gray.800">
          {t('customContent.title')}
        </Heading>
        <HStack>
          <Button as={RouterLink} to="/library/trash" variant="ghost">
            {t('customContent.trash')}
          </Button>
          <Button as={RouterLink} to="/library/ai" variant="outline">
            {t('customContent.generateAi')}
          </Button>
          <Button colorScheme="brand" leftIcon={<AddIcon />} onClick={uploadModal.onOpen}>
            {t('customContent.upload')}
          </Button>
        </HStack>
      </Flex>

      <InputGroup maxW="320px" mb={4}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={t('customContent.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </InputGroup>

      {!!tagsData?.tags.length && (
        <Wrap mb={5} spacing={2}>
          <WrapItem>
            <Tag
              colorScheme={tag === null ? 'brand' : 'gray'}
              borderRadius="full"
              cursor="pointer"
              onClick={() => setTag(null)}
            >
              {t('customContent.allClips')}
            </Tag>
          </WrapItem>
          {tagsData.tags.map((tg) => (
            <WrapItem key={tg}>
              <Tag
                colorScheme={tag === tg ? 'brand' : 'gray'}
                borderRadius="full"
                cursor="pointer"
                textTransform="capitalize"
                onClick={() => setTag(tg === tag ? null : tg)}
              >
                {tg}
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Divider mb={2} />

      {isError ? (
        <Text color="gray.500" py={10} textAlign="center">
          {t('common.error', 'Something went wrong')}
        </Text>
      ) : isLoading ? (
        <VStackSkeleton />
      ) : items.length === 0 ? (
        <Text color="gray.400" py={12} textAlign="center">
          {t('customContent.empty')}
        </Text>
      ) : (
        <Box>
          {items.map((clip) => (
            <HStack
              key={clip.id}
              py={3}
              px={2}
              spacing={4}
              borderBottomWidth="1px"
              borderColor="gray.100"
              _hover={{ bg: 'gray.50' }}
            >
              <Box flex={1} minW={0}>
                <HStack spacing={2}>
                  <Text fontWeight="500" color="gray.800" noOfLines={1}>
                    {clip.title}
                  </Text>
                  <StatusBadge status={clip.processingStatus} />
                </HStack>
                {clip.tags.length > 0 && (
                  <Wrap spacing={1} mt={1}>
                    {clip.tags.map((tg) => (
                      <WrapItem key={tg}>
                        <Tag size="sm" colorScheme="gray" variant="subtle">
                          {tg}
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Box>
              {clip.processingStatus === 'completed' && (
                <PreviewButton
                  id={clip.id}
                  size="xs"
                  resolveUrl={() =>
                    clip.streamUrl
                      ? Promise.resolve(clip.streamUrl)
                      : getContentStreamUrl(clip.id).then((r) => r.streamUrl)
                  }
                />
              )}
              <Text fontSize="sm" color="gray.400" flexShrink={0}>
                {formatTrackDuration(clip.durationSeconds)}
              </Text>
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label={t('common.more', 'More')}
                  icon={<Text fontSize="lg" lineHeight={1}>⋮</Text>}
                  variant="ghost"
                  size="sm"
                />
                <MenuList>
                  <MenuItem onClick={() => setEditing(clip)}>{t('customContent.editClip')}</MenuItem>
                  <MenuItem color="red.500" onClick={() => handleDelete(clip.id)}>
                    {t('common.delete')}
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          ))}

          {hasNextPage && (
            <Flex justify="center" mt={6}>
              <Button variant="outline" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                {t('playlists.loadMore')}
              </Button>
            </Flex>
          )}
        </Box>
      )}

      <UploadContentModal isOpen={uploadModal.isOpen} onClose={uploadModal.onClose} />
      {editing && (
        <EditContentModal content={editing} isOpen={!!editing} onClose={() => setEditing(null)} />
      )}
      <DeleteConflictDialog
        conflict={conflict}
        isOpen={!!conflict}
        isDeleting={deleteContent.isPending}
        onClose={() => {
          setConflict(null);
          setPendingDeleteId(null);
        }}
        onConfirm={confirmDelete}
      />
    </Box>
  );
}

function VStackSkeleton() {
  return (
    <Box>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} height="48px" mb={2} borderRadius="md" />
      ))}
    </Box>
  );
}

export default CustomContentPage;
